// APD Copilot UI state: open/closed, the active scope, and a pending prompt.
//
// The scope is derived from where the person is (the RFQ page they are on, a
// line they pointed at, a catalog issue a page registered). They can narrow it,
// widen it to the workspace, or point it at a line; navigating to a different
// deal resets it, so two deals never share one conversation.

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useRoute } from 'wouter';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import type { ScopeInput } from '@shared/copilot/scope';
import { getCopilotCapability, type CopilotCapability } from './copilot-api';

export interface FocusLine { publicId: string; partNumber: string; lineNumber?: number }

interface CopilotCtx {
  capability: CopilotCapability;
  open: boolean;
  setOpen(v: boolean): void;
  toggle(): void;
  /** The scope Copilot answers about right now. */
  scope: ScopeInput;
  /** What the current page suggests (before the person changes it). */
  pageScope: ScopeInput;
  /** Lines the person can point Copilot at on this page. */
  focusLines: FocusLine[];
  setScope(s: ScopeInput | null): void;
  /** Pages register their own scope (e.g. a catalog issue). */
  registerPageScope(s: ScopeInput | null): void;
  registerFocusLines(lines: FocusLine[]): void;
  /** Open Copilot about something, optionally with a question ready to send. */
  ask(opts: { scope?: ScopeInput; text?: string; mode?: 'auto' | 'research'; autoSend?: boolean }): void;
  pending: { text: string; mode: 'auto' | 'research'; autoSend: boolean; nonce: number } | null;
  clearPending(): void;
}

const NONE: CopilotCapability = { enabled: false, canUse: false, canManageSharedMailboxes: false, gmailConfigured: false };
const Ctx = createContext<CopilotCtx | null>(null);

export const sameScope = (a: ScopeInput, b: ScopeInput) =>
  a.type === b.type && (a.type === 'workspace' || (b.type !== 'workspace' && a.ref === b.ref));

export function CopilotProvider({ children }: { children: ReactNode }) {
  const { getToken, isSignedIn } = useAuth();
  const { data } = useQuery({
    queryKey: ['copilot', 'capability'],
    queryFn: () => getCopilotCapability(() => getToken()),
    enabled: !!isSignedIn,
    staleTime: 10 * 60_000,
    retry: false,
  });
  const capability = data ?? NONE;

  const [location] = useLocation();
  const [onRfq, params] = useRoute('/rfqs/:publicId');
  const [registered, setRegistered] = useState<ScopeInput | null>(null);
  const [focusLines, setFocusLines] = useState<FocusLine[]>([]);
  const pageScope: ScopeInput = useMemo(() => {
    if (registered) return registered;
    if (onRfq && params?.publicId && /^rfq_/.test(params.publicId)) return { type: 'rfq', ref: params.publicId };
    return { type: 'workspace' };
  }, [registered, onRfq, params?.publicId]);

  const [override, setOverride] = useState<ScopeInput | null>(null);
  const [open, setOpenState] = useState<boolean>(() => {
    try { return typeof window !== 'undefined' && window.innerWidth >= 1024 && localStorage.getItem('copilot:open') === '1'; } catch { return false; }
  });
  const [pending, setPending] = useState<CopilotCtx['pending']>(null);

  // A different deal on screen: forget any narrowing chosen for the last one.
  // (Pages unregister their own scope and lines on unmount; child effects run
  // before this one, so clearing those here would erase the new page's.)
  useEffect(() => { setOverride(null); }, [location]);

  const setOpen = useCallback((v: boolean) => {
    setOpenState(v);
    try { localStorage.setItem('copilot:open', v ? '1' : '0'); } catch { /* storage unavailable */ }
  }, []);

  const value: CopilotCtx = {
    capability,
    open: open && capability.canUse,
    setOpen,
    toggle: () => setOpen(!open),
    scope: override ?? pageScope,
    pageScope,
    focusLines,
    setScope: (s) => setOverride(s),
    registerPageScope: setRegistered,
    registerFocusLines: setFocusLines,
    ask: ({ scope, text, mode, autoSend }) => {
      if (scope) setOverride(sameScope(scope, pageScope) ? null : scope);
      if (text) setPending({ text, mode: mode ?? 'auto', autoSend: !!autoSend, nonce: Date.now() });
      setOpen(true);
    },
    pending,
    clearPending: () => setPending(null),
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCopilot(): CopilotCtx {
  const c = useContext(Ctx);
  if (!c) {
    // Outside the provider (tests, isolated renders): inert.
    return {
      capability: NONE, open: false, setOpen: () => {}, toggle: () => {}, scope: { type: 'workspace' }, pageScope: { type: 'workspace' },
      focusLines: [], setScope: () => {}, registerPageScope: () => {}, registerFocusLines: () => {}, ask: () => {}, pending: null, clearPending: () => {},
    };
  }
  return c;
}

/** Let a page tell Copilot what it is showing. */
export function useCopilotPageScope(scope: ScopeInput | null, lines?: FocusLine[]) {
  const { registerPageScope, registerFocusLines } = useCopilot();
  const key = scope ? JSON.stringify(scope) : '';
  const linesKey = lines ? JSON.stringify(lines) : '';
  useEffect(() => {
    if (scope) registerPageScope(scope);
    return () => { if (scope) registerPageScope(null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => {
    if (lines) registerFocusLines(lines);
    return () => { if (lines) registerFocusLines([]); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linesKey]);
}
